import React, { useState, useEffect, useRef } from 'react';
import { Download, X, Loader2 } from 'lucide-react';
import { Property, AppUser } from '../../types';
import { adaptPropertyToTechnicalSheet } from './propertySheetAdapter';
import { PropertyTechnicalSheetDocument } from './PropertyTechnicalSheetDocument';
import { exportTechnicalSheetToPdf } from './pdfSheetExporter';

interface PropertyTechnicalSheetModalProps {
  isOpen: boolean;
  onClose: () => void;
  property: Property;
  appUser?: AppUser | null;
}

export const PropertyTechnicalSheetModal: React.FC<PropertyTechnicalSheetModalProps> = ({
  isOpen,
  onClose,
  property,
  appUser,
}) => {
  const [isExporting, setIsExporting] = useState(false);
  const [scale, setScale] = useState(1);
  const containerRef = useRef<HTMLDivElement>(null);

  // Escalar proporcionalmente en pantallas más pequeñas que 840px para que se vea idéntico a PC
  useEffect(() => {
    if (!isOpen) return;

    const updateScale = () => {
      const screenW = window.innerWidth;
      const padding = 24;
      const availableW = screenW - padding;
      if (availableW < 800) {
        setScale(Math.max(0.35, availableW / 800));
      } else {
        setScale(1);
      }
    };

    updateScale();
    window.addEventListener('resize', updateScale);
    return () => window.removeEventListener('resize', updateScale);
  }, [isOpen]);

  if (!isOpen) return null;

  const sheetData = adaptPropertyToTechnicalSheet(property, appUser);

  const handleDownloadPdf = async () => {
    try {
      setIsExporting(true);
      await new Promise((resolve) => setTimeout(resolve, 80));
      const codeClean = (property.codigo || 'INMUEBLE').replace(/[^a-zA-Z0-9_-]/g, '_');
      await exportTechnicalSheetToPdf('bolivar-technical-sheet', `Ficha_Tecnica_${codeClean}.pdf`);
    } catch (err) {
      console.error('Error al generar el PDF de la ficha técnica:', err);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div
      id="technical-sheet-modal"
      className="fixed inset-0 z-[200] flex flex-col bg-slate-900/85 backdrop-blur-md overflow-y-auto animate-fade-in"
    >
      {/* Barra superior de acciones */}
      <div className="sticky top-0 z-30 flex items-center justify-between px-4 sm:px-6 py-2.5 sm:py-3.5 bg-[#0a2540] text-white border-b border-slate-700 shadow-md print:hidden">
        <div className="flex items-center gap-2 min-w-0">
          <span className="w-2.5 h-2.5 rounded-full bg-[#f59e0b] flex-shrink-0" />
          <h2 className="text-xs sm:text-sm font-bold truncate">
            Ficha Técnica Oficial • <span className="font-mono text-slate-300">{property.codigo}</span>
          </h2>
        </div>

        <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
          <button
            onClick={handleDownloadPdf}
            disabled={isExporting}
            className="inline-flex items-center gap-2 px-3.5 sm:px-4 py-1.5 sm:py-2 rounded-xl bg-[#f59e0b] hover:bg-[#d97706] disabled:bg-amber-300 text-[#0a2540] text-xs font-extrabold transition-all shadow-sm active:scale-95 cursor-pointer whitespace-nowrap"
          >
            {isExporting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin text-[#0a2540]" />
                <span>Generando...</span>
              </>
            ) : (
              <>
                <Download className="w-4 h-4" />
                <span>Descargar PDF</span>
              </>
            )}
          </button>

          <button
            onClick={onClose}
            className="p-1.5 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 transition-colors cursor-pointer"
            title="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Contenedor principal con escalado automático para visualización idéntica a PC en celulares */}
      <div
        ref={containerRef}
        className="flex-1 py-6 px-2 flex justify-center items-start overflow-x-hidden print:p-0 print:m-0 print:bg-white"
      >
        <div
          style={{
            width: `${800 * scale}px`,
            height: 'auto',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <div
            style={{
              transform: `scale(${scale})`,
              transformOrigin: 'top center',
              width: '800px',
              minWidth: '800px',
            }}
          >
            <PropertyTechnicalSheetDocument data={sheetData} />
          </div>
        </div>
      </div>

      {/* Overlay de generación en alta definición */}
      {isExporting && (
        <div className="fixed inset-0 z-[250] bg-slate-950/70 backdrop-blur-xs flex flex-col items-center justify-center gap-3 text-white animate-fade-in">
          <div className="p-4 rounded-2xl bg-[#0a2540] border border-slate-700 shadow-2xl flex flex-col items-center gap-3">
            <Loader2 className="w-8 h-8 text-[#f59e0b] animate-spin" />
            <div className="text-center">
              <p className="text-sm font-extrabold text-white">Generando Ficha Técnica PDF</p>
              <p className="text-xs text-slate-300 mt-0.5">Optimizando gráficos e imágenes...</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
